import { motion } from "framer-motion";
import { useState } from "react";

export function FusionLayersDiagram() {
  const [hoveredLayer, setHoveredLayer] = useState<string | null>(null);

  const layers = [
    { id: "creation-desk", label: "CREATION DESK", sublabel: "Expert capacity on tap" },
    { id: "ai-lab", label: "AI LAB", sublabel: "Engineered AI layer" },
    { id: "platform", label: "PLATFORM", sublabel: "Your Brand's Engine" },
  ];

  const getLayerOpacity = (layerId: string) => {
    if (!hoveredLayer) return 1;
    return hoveredLayer === layerId ? 1 : 0.4;
  };

  return (
    <div className="relative w-full aspect-[3/2] max-w-sm mx-auto">
      {/* Outer Layer - Creation Desk */}
      <motion.div
        className="absolute rounded-3xl cursor-pointer"
        style={{
          top: "0%",
          left: "0%",
          right: "5%",
          bottom: "5%",
          background: "linear-gradient(135deg, hsl(145 25% 72%) 0%, hsl(145 20% 65%) 100%)",
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: getLayerOpacity("creation-desk"),
        }}
        transition={{ duration: 0.6, delay: 0.1, ease: "easeOut" }}
        onMouseEnter={() => setHoveredLayer("creation-desk")}
        onMouseLeave={() => setHoveredLayer(null)}
        whileHover={{ scale: 1.02 }}
      >
        {/* Breathing glow effect */}
        <motion.div
          className="absolute inset-0 rounded-3xl"
          style={{
            boxShadow: "0 0 40px hsl(145 25% 50% / 0.3)",
          }}
          animate={{
            boxShadow: [
              "0 0 40px hsl(145 25% 50% / 0.2)",
              "0 0 60px hsl(145 25% 50% / 0.4)",
              "0 0 40px hsl(145 25% 50% / 0.2)",
            ],
          }}
          transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Label - top left */}
        <motion.p 
          className="absolute top-3 left-4 text-sm font-semibold tracking-wide text-slate-700/90"
          animate={{ opacity: hoveredLayer === "creation-desk" || !hoveredLayer ? 1 : 0.5 }}
        >
          {layers[0].label}
        </motion.p>
        {/* Sublabel - top right */}
        <motion.p 
          className="absolute top-3 right-4 text-xs text-slate-600/80 text-right"
          animate={{ opacity: hoveredLayer === "creation-desk" || !hoveredLayer ? 1 : 0.5 }}
        >
          {layers[0].sublabel}
        </motion.p>
      </motion.div>

      {/* Middle Layer - AI Lab */}
      <motion.div
        className="absolute rounded-3xl cursor-pointer"
        style={{
          top: "22%",
          left: "10%",
          right: "5%",
          bottom: "5%",
          background: "linear-gradient(135deg, hsl(150 30% 55%) 0%, hsl(150 25% 48%) 100%)",
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: getLayerOpacity("ai-lab"),
        }}
        transition={{ duration: 0.6, delay: 0.25, ease: "easeOut" }}
        onMouseEnter={() => setHoveredLayer("ai-lab")}
        onMouseLeave={() => setHoveredLayer(null)}
        whileHover={{ scale: 1.03 }}
      >
        {/* Ripple effect */}
        <motion.div
          className="absolute inset-0 rounded-3xl border-2 border-white/20"
          animate={{
            scale: [1, 1.02, 1],
            opacity: [0.5, 0.2, 0.5],
          }}
          transition={{ duration: 3, repeat: Infinity, ease: "easeInOut", delay: 0.5 }}
        />
        
        {/* Label - top left */}
        <motion.p 
          className="absolute top-3 left-4 text-sm font-semibold tracking-wide text-white/95"
          animate={{ opacity: hoveredLayer === "ai-lab" || !hoveredLayer ? 1 : 0.5 }}
        >
          {layers[1].label}
        </motion.p>
        {/* Sublabel - top right */}
        <motion.p 
          className="absolute top-3 right-4 text-xs text-white/80 text-right"
          animate={{ opacity: hoveredLayer === "ai-lab" || !hoveredLayer ? 1 : 0.5 }}
        >
          {layers[1].sublabel}
        </motion.p>
      </motion.div>

      {/* Core Layer - Platform */}
      <motion.div
        className="absolute rounded-3xl cursor-pointer"
        style={{
          top: "44%",
          left: "20%",
          right: "5%",
          bottom: "5%",
          background: "linear-gradient(135deg, hsl(155 35% 38%) 0%, hsl(160 40% 28%) 100%)",
        }}
        initial={{ scale: 0, opacity: 0 }}
        animate={{ 
          scale: 1, 
          opacity: getLayerOpacity("platform"),
        }}
        transition={{ duration: 0.6, delay: 0.4, ease: "easeOut" }}
        onMouseEnter={() => setHoveredLayer("platform")}
        onMouseLeave={() => setHoveredLayer(null)}
        whileHover={{ scale: 1.05 }}
      >
        {/* Pulsing core glow */}
        <motion.div
          className="absolute inset-0 rounded-3xl"
          animate={{
            scale: [1, 1.04, 1],
            boxShadow: [
              "0 0 20px hsl(155 40% 30% / 0.4), inset 0 0 20px hsl(155 40% 50% / 0.2)",
              "0 0 40px hsl(155 40% 30% / 0.6), inset 0 0 30px hsl(155 40% 50% / 0.3)",
              "0 0 20px hsl(155 40% 30% / 0.4), inset 0 0 20px hsl(155 40% 50% / 0.2)",
            ],
          }}
          transition={{ duration: 2.5, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Inner bright spot */}
        <motion.div
          className="absolute inset-[15%] rounded-2xl bg-gradient-to-br from-white/15 to-transparent"
          animate={{
            opacity: [0.3, 0.5, 0.3],
          }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        />
        
        {/* Label - top left */}
        <motion.p 
          className="absolute top-3 left-4 text-sm font-semibold tracking-wide text-white"
          animate={{ opacity: hoveredLayer === "platform" || !hoveredLayer ? 1 : 0.5 }}
        >
          {layers[2].label}
        </motion.p>
        {/* Sublabel - top right */}
        <motion.p 
          className="absolute top-3 right-4 text-xs text-white/85 text-right"
          animate={{ opacity: hoveredLayer === "platform" || !hoveredLayer ? 1 : 0.5 }}
        >
          {layers[2].sublabel}
        </motion.p>
        
        {/* "You" in the center */}
        <motion.div
          className="absolute inset-0 flex items-center justify-center"
          animate={{ opacity: hoveredLayer === "platform" || !hoveredLayer ? 1 : 0.5 }}
        >
          <motion.span
            className="text-3xl md:text-4xl font-bold text-white/90 tracking-wide"
            animate={{
              scale: [1, 1.05, 1],
              opacity: [0.8, 1, 0.8],
            }}
            transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
          >
            You
          </motion.span>
        </motion.div>
      </motion.div>

      {/* Ambient particles */}
      {[...Array(6)].map((_, i) => (
        <motion.div
          key={i}
          className="absolute w-1.5 h-1.5 rounded-full bg-white/40"
          style={{
            top: `${20 + Math.random() * 60}%`,
            left: `${20 + Math.random() * 60}%`,
          }}
          animate={{
            y: [0, -10, 0],
            opacity: [0.2, 0.6, 0.2],
            scale: [0.8, 1.2, 0.8],
          }}
          transition={{
            duration: 3 + Math.random() * 2,
            repeat: Infinity,
            ease: "easeInOut",
            delay: Math.random() * 2,
          }}
        />
      ))}
    </div>
  );
}
